import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetAccount200Response } from "../../../codegen-api";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { SPACING } from "../../../constants/design/spacing";
import { AccountStateEnum, AuthContext } from "../../../contexts/AuthContext";
import { ConnectWalletContext } from "../../../contexts/ConnectWalletContext";
import { IPerpsMarket } from "../../../contexts/MarketInstrumentContext/useGetMarkets";
import { useAccountLeverage } from "../../../hooks/api/leverage/useAccountLeverage";
import { useToast } from "../../../hooks/toast";
import { parseLeverageInput } from "./leverageInput";
import {
  leverageCap,
  normalizeAccountLeverage,
  resolveMaxLeverage,
} from "../../../utils/leverage";
import { ToastEnum, ToastStatusEnum } from "../../../utils/toast";
import { BaseModal } from "../../BaseModal";
import { Button, ButtonThemeEnum } from "../../Buttons/styles";
import { WarningLabel } from "../style";
import { LeverageModalInfo } from "./LeverageModal.info";
import {
  LeverageButton,
  LeverageButtonsWrapper,
  LeverageInputCard,
  LeverageInputCardLabel,
  LeverageMinMaxRow,
  LeverageModalActions,
  LeverageRangeSlider,
  LeverageSliderOuter,
  LeverageValueInput,
} from "./style";

interface ILeverageModalProps {
  show?: boolean;
  onHide?: () => void;
  accountData?: GetAccount200Response;
  instrument?: IPerpsMarket;
  onUpdateLeverageSuccess: () => void;
  onLeverageUpdated?: (leverage: number) => void;
}

const LEVERAGE_STEPS = [1, 2, 5, 10, 20, 25, 40, 50, 100];
const ALT_LEVERAGE_STEPS = [1, 2, 3, 5];

export function LeverageModal({
  show,
  onHide,
  instrument,
  accountData,
  onUpdateLeverageSuccess,
  onLeverageUpdated,
}: ILeverageModalProps) {
  const { addToast, addErrorToast } = useToast();
  const { updateAccountLeverage } = useAccountLeverage();
  const { t: apiErrors } = useTranslation("apiErrors");
  const { t } = useTranslation("app", {
    keyPrefix: "TradeForm.PerpsTradeForm.LeverageModal",
  });

  const { setShowConnectModal } = useContext(ConnectWalletContext);
  const { accountApiKeyState, account } = useContext(AuthContext);

  const [showReduceWarning, setShowReduceWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inFlightRef = useRef(false);

  const accountLeverage = useMemo(
    () =>
      accountData?.leverages?.find(
        (lev) =>
          lev.instrument_id?.toLowerCase() ===
          instrument?.instrument_id?.toLowerCase()
      )?.leverage,
    [accountData?.leverages, instrument?.instrument_id]
  );

  // Unknown max leverage disables changes (resolveMaxLeverage) instead of
  // capping at a guessed 1x.
  const knownMaxLeverage = resolveMaxLeverage(instrument);
  const unavailable = knownMaxLeverage === undefined;
  const maxLeverage = leverageCap(knownMaxLeverage, accountLeverage);

  const currentLeverage = useMemo(
    () => Math.min(normalizeAccountLeverage(accountLeverage), Math.floor(maxLeverage)),
    [accountLeverage, maxLeverage]
  );

  const maxLeverageSteps = useMemo(() => {
    if (maxLeverage <= 5) {
      return ALT_LEVERAGE_STEPS.filter((step) => step <= maxLeverage);
    }
    return LEVERAGE_STEPS.filter((step) => step <= maxLeverage).slice(-4);
  }, [maxLeverage]);

  const [selectedLeverage, setLeverage] = useState<number>(currentLeverage);
  // Raw text while typing. Clamping every keystroke snapped a cleared field to
  // 1, so typing "5" into it produced "15".
  const [leverageInput, setLeverageInput] = useState<string | null>(null);
  useEffect(() => setLeverage(currentLeverage), [currentLeverage]);

  const sliderFillPercent =
    maxLeverage > 1
      ? ((selectedLeverage - 1) / (maxLeverage - 1)) * 100
      : 100;

  // Info row values.
  //
  // Three states, not two, and the middle one is the one that was wrong: the
  // row printed a confident "0.00 USD" whenever this came back absent, which
  // includes `/account` returning 401 or failing outright.
  //
  //   no accountData          -> still loading; the info row shows a skeleton.
  //   `collaterals: []`       -> a REAL zero. The account loaded and the API
  //                              said it holds no collateral, the same rule
  //                              `Collateral/Token.helpers.ts` applies to a
  //                              token with no row.
  //   `collaterals` absent    -> UNKNOWN. The API omitted the field rather than
  //                              reporting an empty one, which is exactly the
  //                              distinction `rounding.spec.ts:1063` pins by
  //                              passing `collaterals: undefined` to mean "has
  //                              not loaded". An empty array and a missing
  //                              field are different facts and must not collapse.
  //   a row, but no figure    -> UNKNOWN. The API described the row and left
  //                              the number out; we were never told it.
  //
  // The real zero is passed as "0" rather than formatted text so the row runs
  // every balance through the same `floorToDecimalsString` and holds no money
  // literal of its own.
  const collaterals = accountData?.collaterals;
  const collateralRow = collaterals?.[0];
  const availableBalance =
    collateralRow?.available_balance ??
    (collaterals && collaterals.length === 0 ? "0" : undefined);
  const maxPositionValue = (instrument as any)?.max_notional_value;

  const onUpdateLeverage = useCallback(async () => {
    if (!instrument?.instrument_id) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsSubmitting(true);
    setShowReduceWarning(false);
    try {
      const response = await updateAccountLeverage(
        String(instrument.instrument_id),
        selectedLeverage
      );
      if (response.success) {
        addToast({
          type: ToastEnum.SIMPLE,
          header: t("updated_leverage_success_desc"),
          status: ToastStatusEnum.SUCCESS,
        });
        onHide?.();
        onLeverageUpdated?.(selectedLeverage);
        onUpdateLeverageSuccess();
      }
    } catch (error: any) {
      addErrorToast(
        t("updated_leverage_failed_desc"),
        apiErrors(error.message) || "update_leverage_failed"
      );
    } finally {
      inFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    addErrorToast,
    addToast,
    apiErrors,
    instrument?.instrument_id,
    onHide,
    onLeverageUpdated,
    selectedLeverage,
    t,
    updateAccountLeverage,
    onUpdateLeverageSuccess,
  ]);

  const onSetLeverage = useCallback(
    (leverage: number) => {
      const existingPosition = accountData?.positions?.find(
        (pos) => pos.instrument_id === instrument?.instrument_id
      );
      if (
        existingPosition &&
        accountLeverage &&
        leverage < Number(accountLeverage)
      ) {
        setShowReduceWarning(true);
      } else {
        setShowReduceWarning(false);
        setLeverage(leverage);
      }
    },
    [accountData?.positions, accountLeverage, instrument?.instrument_id]
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { text, leverage } = parseLeverageInput(e.target.value, maxLeverage);
      setLeverageInput(text);
      if (leverage !== undefined) onSetLeverage(leverage);
    },
    [onSetLeverage, maxLeverage]
  );

  return (
    <BaseModal
      style={{ width: 420, maxWidth: "calc(100vw - 32px)" }}
      show={show}
      onHide={onHide}
      title="Adjust Leverage"
    >
      {/* Leverage value input card */}
      <LeverageInputCard>
        <LeverageInputCardLabel>Leverage</LeverageInputCardLabel>
        <div style={{ display: "flex", alignItems: "baseline", gap: 0 }}>
          <LeverageValueInput
            type="text"
            step="1"
            min={1}
            max={maxLeverage}
            disabled={unavailable}
            aria-label={t("leverage")}
            value={leverageInput ?? selectedLeverage}
            onChange={onInputChange}
            onBlur={() => setLeverageInput(null)}
            style={{
              fontSize: FONT_SIZE.three,
              width: `${Math.max(1, String(leverageInput ?? selectedLeverage).length)}ch`,
            }}
          />
          <span style={{ fontSize: FONT_SIZE.three, fontWeight: 600, color: "white" }}>x</span>
        </div>

      </LeverageInputCard>

      {/* Slider */}
      <LeverageSliderOuter>
        <LeverageRangeSlider
          min={1}
          max={maxLeverage}
          disabled={unavailable}
          aria-label={t("leverage")}
          value={selectedLeverage}
          fillPercent={sliderFillPercent}
          onChange={(e) => onSetLeverage(Number(e.target.value))}
        />
        <LeverageMinMaxRow>
          <span>1x</span>
          <span>{unavailable ? "—" : `${maxLeverage}x`}</span>
        </LeverageMinMaxRow>
      </LeverageSliderOuter>

      {/* Quick-select buttons */}
      <LeverageButtonsWrapper>
        {maxLeverageSteps.map((l) => (
          <LeverageButton
            key={l}
            $selected={l === selectedLeverage}
            disabled={unavailable}
            onClick={() => onSetLeverage(l)}
          >
            {l}x
          </LeverageButton>
        ))}
      </LeverageButtonsWrapper>

      <LeverageModalInfo
        maxPositionValue={maxPositionValue}
        availableBalance={availableBalance}
        balanceLoading={!accountData}
        unavailableText={unavailable ? t("max_leverage_unavailable") : undefined}
      />

      {/* Reduce warning */}
      {showReduceWarning && (
        <WarningLabel style={{ marginBottom: SPACING.two }}>
          {t("cannot_reduce", { asset: instrument?.underlying_asset })}
        </WarningLabel>
      )}

      {/* Actions */}
      <LeverageModalActions>
        <Button buttonTheme={ButtonThemeEnum.NEUTRAL2} onClick={onHide}>
          Cancel
        </Button>
        {accountApiKeyState === AccountStateEnum.OK ? (
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            onClick={onUpdateLeverage}
            disabled={isSubmitting || unavailable}
          >
            {isSubmitting ? "Confirming…" : "Confirm"}
          </Button>
        ) : (
          <Button
            buttonTheme={ButtonThemeEnum.HIGHLIGHT}
            onClick={() => setShowConnectModal(true)}
          >
            {!account ? t("connect_wallet") : t("complete_sign_in")}
          </Button>
        )}
      </LeverageModalActions>
    </BaseModal>
  );
}
